import { QueryKey, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  BlogCommentEntity,
  ServerListResponse,
  ServerSingleResponse,
} from "../../types";
import { AxiosResponse } from "axios";

type CommentPostPromise = Promise<
  AxiosResponse<ServerSingleResponse<BlogCommentEntity>>
>;
type UseDeleteCommentsonfigProps = (obj: {
  articleId: number;
  commentId: number;
}) => CommentPostPromise;

export function useDeleteComment(
  queryKey: QueryKey,
  onDeleteComment: UseDeleteCommentsonfigProps,
) {
  const queryClient = useQueryClient();

  const { mutate } = useMutation({
    mutationFn: (comment: BlogCommentEntity) =>
      onDeleteComment({ articleId: comment.object_id, commentId: comment.id }),

    onMutate: (comment) => {
      const prevState = queryClient.getQueryData(queryKey) as AxiosResponse<
        ServerListResponse<BlogCommentEntity>
      >;
      const prevReleaseCommentsData = prevState.data.payload.results;

      const newState = {
        ...prevState,
        data: {
          payload: {
            results: deleteComment(prevReleaseCommentsData, comment),
          },
        },
      };

      queryClient.setQueryData(queryKey, newState);

      return newState;
    },
    onError: (_, __, context) => {
      queryClient.setQueryData(queryKey, context);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return mutate;
}

function deleteComment(
  comments: BlogCommentEntity[],
  deletedComment: BlogCommentEntity,
): BlogCommentEntity[] {
  return comments
    .filter((comment) => {
      return comment.id !== deletedComment.id;
    })
    .map((comment) => ({
      ...comment,
      replies: deleteComment(comment.replies, deletedComment),
    }));
}
